/* What the app is allowed to interrupt you about, and when.
 *
 * Pure: preferences in, a list of notifications out. No Capacitor, no
 * Notification API, no storage — `notify.js` owns delivery and `native.js`
 * owns the platform. Splitting it this way is what makes the awkward part
 * (which days to schedule, and what each one should say) testable in Vitest
 * instead of only on a phone.
 *
 * Two rules shape everything here:
 *
 * 1. **Off until asked.** Every channel starts false. An app that notifies by
 *    default gets its notifications switched off at the OS level within a
 *    week, and that switch is permanent in practice — nobody ever goes back
 *    into system settings to re-enable an app they muted. Consent asked for
 *    once, in context, is worth more than reach taken by default.
 * 2. **A reminder you didn't need is worse than no reminder.** Nothing is
 *    scheduled for a day already trained, and nothing is scheduled at all for
 *    someone with no streak to defend and no goal to miss — see
 *    `practiceSchedule`. The failure mode of a habit app is becoming the
 *    thing the user mutes.
 */
import { dayKey, goalOf, streakDefense, trainedDaysIn, usualHour, weekProgress, weekStart } from "./learn/goals.js";
import { t } from "./i18n.js";

/* A short list rather than a free time picker. Every entry is an evening or a
 * commute — the times a 3-minute session actually fits into — and a picker
 * would mostly be an opportunity to choose 03:00 by accident. */
export const REMIND_TIMES = ["07:30", "08:30", "12:30", "17:30", "18:30", "19:30", "20:30", "21:30"];
export const DEFAULT_REMIND_AT = "19:30";

/* How far ahead the practice reminder is scheduled. Local notifications have
 * no server behind them, so the app can only queue what it can foresee, and
 * it re-queues every time it opens.
 *
 * That the queue RUNS OUT is a feature, not a limitation to work around: a
 * user who stops opening the app stops being reminded after a week, because
 * nothing is left to reschedule. An app with a server would keep pushing into
 * that silence for months. This one cannot, and shouldn't. */
export const HORIZON_DAYS = 7;

/* Fixed ids, so scheduling is idempotent: cancel all of them, then queue the
 * ones that still apply. Slot i is "i days from today", which means yesterday's
 * slot 1 becomes today's slot 0 on the next run and simply gets rewritten. */
export const PRACTICE_IDS = [101, 102, 103, 104, 105, 106, 107];

/* G7's recap lands Monday morning, because that is when a finished week reads
 * as a finished week. One notification, its own id, re-queued like the rest. */
export const RECAP_AT = "09:00";
export const RECAP_ID = 201;

export function emptyPrefs() {
  return {
    updatedAt: 0,

    /* F5 · the daily practice reminder. `device` is the only channel that
     * makes sense for it — an email that says "train today" arrives after the
     * moment has passed. */
    practice: { device: false, at: DEFAULT_REMIND_AT },

    /* G7 · the weekly recap, in two channels. `email` is the only one the app
     * cannot deliver by itself; Settings shows it only when `config/notifications`
     * says a mail sender actually exists, so the app never offers to email
     * somebody from an installation that cannot send. */
    recap: { device: false, email: false },

    /* Proof-of-possession for the one-click unsubscribe link in every recap
     * email. Minted when email is first switched on, mailed inside the link,
     * and checked by firestore.rules — which is what lets somebody stop the
     * mail without signing in, from a machine they never signed in on.
     *
     * An identifier, not a preference: `mergePrefs` keeps whichever side has
     * one rather than letting the newest statement drop it, because losing it
     * would silently invalidate every unsubscribe link already in an inbox. */
    emailToken: null,

    /* When the OS permission prompt was last shown. The prompt is one-shot on
     * both platforms — a denial is final until the user goes into system
     * settings — so the app gets exactly one good moment to ask, and must not
     * spend it on a user who hasn't opted in yet. */
    askedAt: null,
  };
}

/* Tolerates a blob written before a key existed, which is the normal path for
 * every stored copy the first time a new kind ships. */
export function normalizePrefs(raw) {
  const base = emptyPrefs();
  if (!raw || typeof raw !== "object") return base;
  const at = REMIND_TIMES.includes(raw.practice?.at) ? raw.practice.at : base.practice.at;
  return {
    updatedAt: Number.isFinite(raw.updatedAt) ? raw.updatedAt : 0,
    /* W2d · `atChosen` is stored only once the user has picked a time, so a
     * prefs blob that never did keeps exactly its old shape. */
    practice: { device: Boolean(raw.practice?.device), at, ...(raw.practice?.atChosen ? { atChosen: true } : {}) },
    recap: { device: Boolean(raw.recap?.device), email: Boolean(raw.recap?.email) },
    emailToken: typeof raw.emailToken === "string" && raw.emailToken.length >= 20 ? raw.emailToken : null,
    askedAt: Number.isFinite(raw.askedAt) ? raw.askedAt : null,
  };
}

/* 36 characters from the platform CSPRNG. Long enough that the rules can
 * insist on >= 20 and know a guess is hopeless. */
export const newEmailToken = () =>
  globalThis.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;

/* Notification prefs are a *statement*, like the profile and the goal in
 * sync.js — the whole object moves together and the most recent one wins.
 * Merging field-wise would build a setting nobody chose: reminders on from
 * the phone at a time picked on the laptop. Ties go to local, matching the
 * "this device's prefs win" rule the rest of the app already uses. */
export function mergePrefs(local, remote) {
  if (!remote) return normalizePrefs(local);
  if (!local) return normalizePrefs(remote);
  const winner = normalizePrefs((local.updatedAt || 0) >= (remote.updatedAt || 0) ? local : remote);
  /* ...except the unsubscribe token, which is not a statement about anything.
   * It is the secret already sitting in the user's inbox, and a newer
   * statement from a device that never had it would invalidate every link
   * already sent. */
  winner.emailToken = normalizePrefs(local).emailToken || normalizePrefs(remote).emailToken;
  return winner;
}

export const anythingOn = (prefs) => Boolean(prefs?.practice?.device || prefs?.recap?.device || prefs?.recap?.email);

const minutesOf = (hhmm) => {
  const [h, m] = String(hhmm || "").split(":").map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

/* Local wall-clock, deliberately. `at` is "19:30 where the user is", so it has
 * to be built from local date parts — the same reason goals.js#dayKey exists
 * rather than slicing an ISO string. A UTC-based version would fire at
 * lunchtime for half the world. */
function atOn(dayOffset, hhmm, now) {
  const mins = minutesOf(hhmm);
  if (mins == null) return null;
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + dayOffset);
  d.setMinutes(mins);
  return d.getTime();
}

/* W2d · the offered slot for a usual training hour: the latest slot at or
 * before the start of that hour, and never more than two hours before it. A
 * nudge after the session it is for is useless; one long before it is noise.
 * Null when no slot fits, so the chosen or default time stands. */
export function usualRemindAt(ls) {
  const hour = usualHour(ls);
  if (hour === null) return null;
  const target = hour * 60;
  let pick = null;
  for (const slot of REMIND_TIMES) {
    const mins = minutesOf(slot);
    if (mins <= target && target - mins <= 120 && (pick === null || mins > minutesOf(pick))) pick = slot;
  }
  return pick;
}

/* The practice reminder's time: one the user picked always wins; otherwise
 * their habit; otherwise the default. */
export function remindTimeFor(prefs, ls) {
  if (prefs?.practice?.atChosen) return prefs.practice.at;
  return usualRemindAt(ls) || prefs?.practice?.at || DEFAULT_REMIND_AT;
}

/* What today's reminder should actually say.
 *
 * Only slot 0 gets a specific message: it is the only day whose state the app
 * knows at scheduling time. Promising "3 of 4 this week" for next Thursday
 * would be a guess, and a reminder that turns out to be wrong about your own
 * numbers is worse than a generic one. */
export function practiceMessage(ls, now = Date.now(), dueHint = null) {
  const streak = streakDefense(ls, now);
  const week = weekProgress(ls, now);
  /* F4 · a freeze already covers yesterday, but only if today gets trained —
   * the freeze itself is spent the moment `bumpStreak` sees this session, not
   * before. Telling someone "the streak is alive" when a day already slipped
   * would be true right up until it wasn't, so this gets its own line rather
   * than folding into the ordinary at-risk copy below. */
  if (streak.freezeWillCover) {
    return { title: t("notify.freezeLeft", { days: streak.count }), body: t("notify.freezeBody") };
  }
  if (streak.atRisk) {
    return { title: t("notify.onTheLine", { count: streak.count }), body: t("notify.streakBody") };
  }
  /* W2d · name the opening. notify.js cannot see the catalog, so the Learn tab
   * leaves a same-day hint (notify.js#saveDueHint); a hint from another day is
   * ignored rather than trusted, so this is never wrong about today. The two
   * branches above still win: a streak about to break outranks any opening. */
  if (dueHint && dueHint.day === dayKey(now) && dueHint.cards > 0 && dueHint.course) {
    return {
      title: t("notify.openingDue", { course: dueHint.course, minutes: dueHint.minutes || 1 }),
      body: t("notify.openingDueBody", { count: dueHint.cards }),
    };
  }
  if (!week.met && week.daysLeft > 0 && week.done > 0) {
    const left = week.target - week.done;
    return {
      title: t("notify.weekProgress", { done: week.done, target: week.target }),
      body: t("notify.toGoal", { count: left }),
    };
  }
  return { title: t("notify.timeToTrain"), body: t("notify.fewCards") };
}

/* A getter, not a constant: this module is imported once and a plain string
 * would freeze at whatever the locale was at import time. */
const GENERIC = {
  get title() {
    return t("notify.timeToTrain");
  },
  get body() {
    return t("notify.cardsWaiting");
  },
};

/* The whole scheduling decision, as data. `notify.js` hands the result
 * straight to the platform; nothing else in the app has to know how a
 * reminder is chosen.
 *
 * Skips a day when: reminders are off, the time has already passed (only ever
 * possible for today), or that day was already trained. The last one is why
 * this takes the learn state at all — being reminded to do the thing you have
 * just done is the single most annoying notification an app can send, and the
 * information to avoid it is already on disk. */
/* G7 · "your week is ready", the Monday after a week worth reading about.
 *
 * The gate is the whole design. A notification that opens the app onto no
 * recap is a broken promise, and `recap.js` returns null for a blank week — so
 * this checks the week the notification will actually be *about*: the seven
 * days ending at the moment it fires. Trained at least one day in that window
 * and a recap is guaranteed to exist; trained none and nothing is queued.
 *
 * It is exact rather than a heuristic, and it self-corrects: the queue is
 * rebuilt on every app open and after every session, so training on Thursday
 * schedules the Monday ping that Wednesday could not justify.
 *
 * Someone who only plays online and never drills is the one case this
 * under-serves — their recap exists but no ping announces it. That is the safe
 * direction to be wrong in: a missing notification, never an empty one. */
export function recapSchedule({ prefs, ls, now = Date.now() } = {}) {
  if (!prefs?.recap?.device) return [];
  const mins = minutesOf(RECAP_AT);
  const monday = weekStart(now);
  monday.setMinutes(mins);
  // Already past this Monday's slot? Then the next one is a week out.
  if (monday.getTime() <= now) monday.setDate(monday.getDate() + 7);
  const at = monday.getTime();

  // The week it will report on: the seven days immediately before it fires.
  const covered = new Date(at);
  covered.setHours(0, 0, 0, 0);
  covered.setDate(covered.getDate() - 7);
  if (trainedDaysIn(ls, covered.getTime()).days === 0) return [];

  return [{ id: RECAP_ID, at, title: t("notify.weekTitle"), body: t("notify.weekBody"), kind: "recap" }];
}

export function practiceSchedule({ prefs, ls, now = Date.now(), horizon = HORIZON_DAYS, dueHint = null } = {}) {
  if (!prefs?.practice?.device) return [];
  const { history } = goalOf(ls);
  const out = [];
  for (let i = 0; i < Math.min(horizon, PRACTICE_IDS.length); i += 1) {
    const at = atOn(i, remindTimeFor(prefs, ls), now);
    if (at == null || at <= now) continue;
    const key = dayKey(at);
    if ((history[key] || 0) > 0) continue;
    const { title, body } = i === 0 ? practiceMessage(ls, now, dueHint) : GENERIC;
    out.push({ id: PRACTICE_IDS[i], at, title, body, kind: "practice" });
  }
  return out;
}
